import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApplicationFilterState } from "../components/ApplicationsFilter/ApplicationsFilter";
import { ErrorToast, SuccessToast } from "../components/toastMessage";
import { _axios } from "../utils/axios";

export type TApplication = {
  id: number;
  userId: number;
  status: string;
  createdAt: string;
  updatedAt: string;
  submittedAt?: string;
  applicationId: string;
  project_name?: string;
  plot_owner_s_name?: string;
  isActionAllowed?: boolean; 
  daysLeftForAction?: number | null;
};

export const level1ApplicationsKeyStore = {
  savedDemandNoteData: (...args: (number | string | undefined)[]) => [
    "level-1-demand-note-data",
    ...args,
  ],
  lastReview: (id?: number | string) => ["level-1-application-last-review", id],
  allReviews: (id?: number | string) => ["level-1-application-all-reviews", id],
  id: (arg: number | string) => ["level-1-application", arg],
  decisionMaps: (arg?: number | string) => ["level-1-decision-maps", arg],
  counts: (...args: (number | string | Record<string, unknown>)[]) => [
    "level-1-applications-counts",
    ...args,
  ],
  list: (...args: (number | string | Record<string, unknown>)[]) => [
    "level-1-applications",
    ...args,
  ],
  stats: (...args: (number | string | Record<string, unknown>)[]) => [
    "level-1-applications-stats",
    ...args,
  ],
};

export function useApplicationStatsForLevel1({
  filters,
}: {
  filters?: ApplicationFilterState;
}) {
  return useQuery({
    queryKey: level1ApplicationsKeyStore.stats(filters || {}),
    queryFn: () =>
      _axios.post<{
        success: true;
        hasMore?: boolean;
        data: { status: string; count: number }[];
      }>("/level-1-application-approval/stats", { filters }),
    staleTime: 2 * 60 * 1000,
    refetchInterval: 2 * 60 * 1000,
    select: (d) => d.data,
  });
}

export function useApplicationCountForLevel1({
  search,
  filters,
}: {
  search?: string;
  filters?: ApplicationFilterState;
}) {
  return useQuery({
    queryKey: level1ApplicationsKeyStore.counts(search || "", filters || {}),
    queryFn: () =>
      _axios.post<{
        success: true;
        hasMore?: boolean;
        data: number;
      }>("/level-1-application-approval/count", { search, filters }),
    staleTime: 2 * 60 * 1000,
    refetchInterval: 2 * 60 * 1000,
    select: (d) => d.data,
  });
}

export function useApplicationsForLevel1({
  page,
  limit,
  search,
  sort,
  sortVal,
  filters,
}: {
  page: number;
  limit: number;
  search?: string;
  sort: string;
  sortVal: number;
  filters?: ApplicationFilterState;
}) {
  return useQuery({
    queryKey: level1ApplicationsKeyStore.list(
      page,
      limit,
      sort,
      sortVal,
      search || "",
      filters || {}
    ),
    queryFn: () =>
      _axios.post<{
        success: true;
        hasMore?: boolean;
        data: TApplication[];
      }>("/level-1-application-approval/list", {
        sort,
        sortVal,
        search,
        page,
        limit,
        filters: filters || {},
      }),
    staleTime: 2 * 60 * 1000,
    refetchInterval: 2 * 60 * 1000,
    select: (d) => d.data,
  });
}

export function useApplicationForLevel1({ id }: { id: number | string }) {
  return useQuery({
    queryKey: level1ApplicationsKeyStore.id(id),
    queryFn: () =>
      _axios.get<{
        success: true;
        data: TApplication & Record<string, unknown>;
      }>(`/level-1-application-approval/${id}`),
    select: (d) => d?.data?.data,
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

export function useLevel1DecisionMaps({ id }: { id: undefined | number }) {
  return useQuery({
    enabled: !!id,
    queryKey: level1ApplicationsKeyStore.decisionMaps(id),
    queryFn: () =>
      _axios.get<{
        success: true;
        data: {
          nocDecisions: Record<string, boolean>;
          documentDecisions: Record<string, boolean>;
        };
      }>(`/level-1-application-approval/decision-map/${id}`),
    select: (res) => res?.data?.data,
  });
}

export function useLevel1UpdateDocumentDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      docId: number;
      decision: 0 | 1;
      applicationId: number;
    }) =>
      _axios.post("/level-1-application-approval/update-doc-decision", payload),
    onSettled: (_, __, { applicationId }) =>
      qc.invalidateQueries({
        queryKey: level1ApplicationsKeyStore.decisionMaps(applicationId),
        exact: true,
      }),
    onError: () => {
      ErrorToast("Something went wrong!");
    },
    onSuccess: () => {
      SuccessToast("Updated!");
    },
  });
}

export function useLevel1UpdateNocDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      nocId: number;
      decision: 0 | 1;
      applicationId: number;
    }) =>
      _axios.post("/level-1-application-approval/update-noc-decision", payload),
    onSettled: (_, __, { applicationId }) =>
      qc.invalidateQueries({
        queryKey: level1ApplicationsKeyStore.decisionMaps(applicationId),
        exact: true,
      }),
    onError: () => {
      ErrorToast("Something went wrong!");
    },
    onSuccess: () => {
      SuccessToast("Updated!");
    },
  });
}

export function useLevel1LastReview({ id }: { id?: number }) {
  return useQuery({
    queryKey: level1ApplicationsKeyStore.lastReview(id),
    enabled: !!id,
    queryFn: () =>
      _axios.get<{
        success: true;
        data: {
          fieldsToChange?: number[];
          review?: { comment_text?: string; id?: number };
        };
      }>(`/level-1-application-approval/get-current-review/${id}`),
    select: (result) => result.data?.data,
  });
}

export type TReviewWithReviewer = {
  id: number;
  date: string;
  comment_text: string;
  level: string;
  reviewer?: { name: string; role: string };
};

export function useLevel1AllReviews({ id }: { id?: number }) {
  return useQuery({
    queryKey: level1ApplicationsKeyStore.allReviews(id),
    enabled: !!id,
    queryFn: () =>
      _axios.get<{
        success: true;
        data: TReviewWithReviewer[];
      }>(`/level-1-application-approval/get-all-reviews/${id}`),
    select: (result) => result.data?.data,
  });
}

export function useSaveLevel1Review() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      applicationId: number;
      comment: string;
      fieldsToChange: number[];
    }) => _axios.post("/level-1-application-approval/save-review", data),
    onSuccess: () => SuccessToast("Saved!"),
    onError: () => ErrorToast("Something went wrong while saving review!"),
    onSettled: (_, __, { applicationId }) => {
      qc.invalidateQueries({
        queryKey: level1ApplicationsKeyStore.lastReview(applicationId),
      });
      qc.invalidateQueries({
        queryKey: level1ApplicationsKeyStore.allReviews(applicationId),
      });
    },
  });
}

export function useLevel1RejectApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      applicationId: number;
      user_id: number;
      application_id: string;
    }) => _axios.post("/level-1-application-approval/reject-application", data),
    onSettled: (_, __, { applicationId }) => {
      qc.invalidateQueries({
        queryKey: level1ApplicationsKeyStore.id(applicationId),
      });
      qc.invalidateQueries({ queryKey: level1ApplicationsKeyStore.list() });
      qc.invalidateQueries({ queryKey: level1ApplicationsKeyStore.stats() });
    },
    onSuccess: () => SuccessToast("Application rejected successfully!"),
    onError: () =>
      ErrorToast("Something went wrong while rejecting application!"),
  });
}

export function useLevel1ReturnApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      applicationId: number;
      user_id: number;
      application_id: string;
    }) => _axios.post("/level-1-application-approval/return-application", data),
    onSettled: (_, __, { applicationId }) => {
      qc.invalidateQueries({
        queryKey: level1ApplicationsKeyStore.id(applicationId),
      });
      qc.invalidateQueries({ queryKey: level1ApplicationsKeyStore.list() });
      qc.invalidateQueries({ queryKey: level1ApplicationsKeyStore.stats() });
    },
    onSuccess: () => SuccessToast("Application returned successfully!"),
    onError: () =>
      ErrorToast("Something went wrong while returning application!"),
  });
}

export type TSavedDemandNoteData = {
  municipality?: string;
  tehsil?: string;
  district?: string;
  dueDate?: string;
  applicantName?: string;
  layoutName?: string;
  area?: number;
  mauja?: string;
  khasaraNo?: string;
  constructionArea?: number;
  particular1Rate?: number;
  particular1Total?: number;
  particular2Rate?: number;
  particular2Total?: number;
  totalAmount?: number;
  plotNo?: string;
  scrutinyCharges?: number;
  conditions?: string;
};

export function useSavedDemandNoteDataForLevel2({
  applicationId,
  reviewId,
}: {
  applicationId?: number;
  reviewId?: number;
}) {
  return useQuery({
    queryKey: level1ApplicationsKeyStore.savedDemandNoteData(
      applicationId,
      reviewId
    ),
    select: (d) => d.data?.data,
    queryFn: () =>
      _axios.get<{ success: true; data?: TSavedDemandNoteData }>(
        "/level-1-application-approval/get-saved-demand-note-data",
        {
          params: { applicationId, reviewId },
        }
      ),
  });
}

export function useSaveDemandNoteDataForLevel1() {
  return useMutation({
    mutationFn: (data: {
      applicationId: number;
      reviewId: number;
      data: Record<string, unknown>;
    }) =>
      _axios.post<{ success: true; data: number[] }>(
        "level-1-application-approval/save-demand-note-data",
        data
      ),
    onError: () =>
      ErrorToast("Something went wrong while generating sample demand note!"),
  });
}

export function useForwardApplicationForLevel1() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { applicationId: number; reviewId: number }) =>
      _axios.post<{ success: true }>(
        "level-1-application-approval/approve-application",
        data
      ),
    onSuccess: (_, { applicationId }) => {
      SuccessToast("Application Forwarded!");
      qc.invalidateQueries({
        queryKey: level1ApplicationsKeyStore.id(applicationId),
      });
      qc.invalidateQueries({ queryKey: level1ApplicationsKeyStore.list() });
      qc.invalidateQueries({ queryKey: level1ApplicationsKeyStore.stats() });
    },
    onError: () =>
      ErrorToast("Something went wrong while forwarding application!"),
  });
}
